import type { AssistantDigest, EmployeeDetail, SearchResults } from '@skhub/contracts';
import { fmtDate, num } from '@/lib/format';

/**
 * The IT Assistant's "brain": turns a typed question into an answer built only from the digest
 * the API computed with SQL. No guessing and no language model: if a question is not understood,
 * the assistant says so and offers questions it can answer exactly.
 */

export type Tone = 'danger' | 'warn' | 'ok' | 'info' | 'neutral';

export interface AnswerRow {
  key: string;
  primary: string;
  /** Monospace code shown before the primary text (asset tag, employee ID, licence tag). */
  code?: string;
  secondary?: string;
  /** Right-aligned figure, e.g. "4 devices" or "12 days left". */
  badge?: string;
  tone?: Tone;
  to?: string;
  chips?: string[];
}

export interface AnswerSection {
  heading?: string;
  rows: AnswerRow[];
}

export interface Answer {
  title: string;
  text: string;
  count?: number;
  tone?: Tone;
  sections: AnswerSection[];
  link?: { label: string; to: string };
  followUps?: string[];
  /** Not one of the checks: the launcher searches the register for this term and calls lookupAnswer. */
  lookup?: string;
}

// ── Question parsing ─────────────────────────────────────────

const WORD_NUMBERS: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, fifteen: 15, thirty: 30, sixty: 60, ninety: 90 };

export function normalise(q: string) {
  return q
    .toLowerCase()
    .replace(/licen[cs]e/g, 'licence')
    .replace(/\b(one|two|three|four|five|six|seven|eight|nine|ten|fifteen|thirty|sixty|ninety)\b/g, (w) => String(WORD_NUMBERS[w]))
    .replace(/\s+/g, ' ')
    .trim();
}

/** A time window in days named in the question ("in 15 days", "next 2 weeks", "this month"), or null. */
export function parseDays(q: string): number | null {
  let m = /(\d+)\s*(?:days?|d)\b/.exec(q);
  if (m) return Number(m[1]);
  m = /(\d+)\s*weeks?/.exec(q);
  if (m) return Number(m[1]) * 7;
  m = /(\d+)\s*months?/.exec(q);
  if (m) return Number(m[1]) * 30;
  m = /(\d+)\s*quarters?/.exec(q);
  if (m) return Number(m[1]) * 90;
  m = /(\d+)\s*(?:years?|yrs?)\b/.exec(q);
  if (m) return Number(m[1]) * 365;
  if (/\b(this|next|a|one) week\b/.test(q)) return 7;
  if (/\b(this|next|a|one) month\b/.test(q)) return 30;
  if (/\b(this|next|a|one) quarter\b/.test(q)) return 90;
  if (/\b(this|next|a|one) year\b/.test(q)) return 365;
  if (/\btomorrow\b/.test(q)) return 1;
  if (/\btoday\b/.test(q)) return 0;
  return null;
}

/** A look-back window ("in the last 30 days", "past 2 weeks"): things that already happened, in days. */
export function parsePastDays(q: string): number | null {
  const m = /\b(?:last|past|previous)\s+(\d+)\s*(days?|weeks?|months?|years?)\b/.exec(q);
  if (!m) return null;
  const unit = m[2]!.startsWith('week') ? 7 : m[2]!.startsWith('month') ? 30 : m[2]!.startsWith('year') ? 365 : 1;
  return Number(m[1]) * unit;
}

/** Minimum count named in the question ("2 or more", "more than 3", "at least 4"), or null. */
export function parseAtLeast(q: string): number | null {
  let m = /(\d+)\s*(?:\+|or more|and more|or above|and above|or over|plus)/.exec(q);
  if (m) return Number(m[1]);
  m = /(?:more than|over|above|greater than|>)\s*(\d+)/.exec(q);
  if (m) return Number(m[1]) + 1;
  m = /(?:at least|minimum of|min|>=)\s*(\d+)/.exec(q);
  if (m) return Number(m[1]);
  return null;
}

const has = (q: string, re: RegExp) => re.test(q);

const RE = {
  help: /^(help( me)?|hi|hello|hey|what can you do|commands?)[!.?]*$/,
  overview: /\b(summary|summarise|summarize|overview|health|odd|anomal\w*|issues?|problems?|everything|all checks|data quality|scan|check all|what('s| is) wrong)\b/,
  duplicate: /\b(dup\w*|same|twice|repeat\w*|clash\w*|conflict\w*|double)\b/,
  serial: /\bserials?\b/,
  missing: /\b(no|missing|without|blank|empty|not recorded)\b/,
  person: /\b(employees?|people|persons?|staff|users?|emails?|names?|emp)\b/,
  assetWord: /\b(assets?|devices?|laptops?|hardware|items?|equipment|machines?|tags?)\b/,
  laptop: /\blaptops?\b/,
  multiId: /\b(multiple|more than 1|several|many|other|extra|old|previous|alternate)\s+(employee\s+)?ids?\b|\b\d+\s*(or more\s+)?(employee\s+)?ids?\b|\balias\w*\b|\b(intern|overseas|contractor)\s+ids?\b|\bids? (each|per person)\b/,
  // Person-anchored: "10 days left" or "inactive licences" are not about former staff.
  former: /\b(former|resigned|ex[- ]?employees?|exited|leavers?|terminated|offboarded)\b|\b(who|people|employees?|staff|users?)\b.*\b(left|inactive)\b|\binactive (employees?|people|staff|users?)\b/,
  licence: /\b(licence\w*|software|subscriptions?|renewals?|renew|saas|seats?)\b/,
  warranty: /\b(warrant\w*|amc|support contract|end of life|eol|coverage)\b/,
  expired: /\b(expired|lapsed|overdue|past due|already|out of|ended|no longer)\b/,
  expiring: /\b(expir\w*|due|ending|end|renew\w*|upcoming|soon|reach\w*|going to|about to|next|within|coming)\b/,
  multiAsset: /\b(multiple|many|more than 1|several|most|extra|2 or more)\b/,
  // "Assets over 5 years old" is about age, not how many each person holds.
  age: /\b(old|older|age|aged|years?|months?|purchased|bought|since)\b/,
};

// ── Row builders ─────────────────────────────────────────────

type Digest = AssistantDigest;
type Person = Digest['multiAssetHolders'][number] | Digest['duplicatePeople'][number]['people'][number];

const plural = (n: number, one: string, many = `${one}s`) => `${num(n)} ${n === 1 ? one : many}`;

const personRow = (p: Person, extra: Partial<AnswerRow> = {}): AnswerRow => ({
  key: `p-${p.id}-${extra.badge ?? ''}`,
  code: p.employeeCode,
  primary: p.displayName,
  secondary: [p.team ?? 'No team recorded', p.email, p.isActive ? null : 'Former employee'].filter(Boolean).join(' · '),
  to: `/employees/${p.id}`,
  ...extra,
});

const assetRow = (a: Digest['noSerial'][number], extra: Partial<AnswerRow> = {}): AnswerRow => ({
  key: `a-${a.id}`,
  code: a.assetTag,
  primary: a.name,
  secondary: [a.assetType, a.status, a.serialNumber ? `S/N ${a.serialNumber}` : null, a.assignee ? `with ${a.assignee}` : null].filter(Boolean).join(' · '),
  to: `/assets/${a.id}`,
  ...extra,
});

const daysText = (d: number) => (d < 0 ? `${plural(-d, 'day')} ago` : d === 0 ? 'today' : `in ${plural(d, 'day')}`);
const dayTone = (d: number): Tone => (d < 0 ? 'danger' : d <= 15 ? 'warn' : 'info');

// ── Answers ──────────────────────────────────────────────────

export const SUGGESTIONS = [
  'Give me a data health summary',
  'Show me expired licences',
  'Licences expiring in 15 days',
  'Employees with 2 or more assets',
  'Duplicate asset IDs',
  'Employees with multiple IDs',
  'Warranty ending in 30 days',
  'Assets without a serial number',
];

export function helpAnswer(): Answer {
  return {
    title: 'What I can check',
    text: 'I read the live register and answer with exact records. Ask about any check below (change the numbers freely: "licences expiring in 45 days"), or look anything up by name, employee ID, asset tag, serial number or product.',
    sections: [
      {
        rows: [
          { key: 'h1', primary: 'Duplicates', secondary: 'Asset tags, serial numbers, employee emails, names and IDs', tone: 'info', chips: ['Duplicate asset IDs', 'Duplicate employees'] },
          { key: 'h2', primary: 'People', secondary: 'Multiple assets, multiple employee IDs, former employees still holding kit', tone: 'info', chips: ['Employees with 2 or more assets', 'Employees with multiple IDs'] },
          { key: 'h3', primary: 'Software & licences', secondary: 'Expired, or expiring within any number of days', tone: 'info', chips: ['Show me expired licences', 'Licences expiring in 15 days'] },
          { key: 'h4', primary: 'Hardware warranty', secondary: 'Expired, or ending within any number of days', tone: 'info', chips: ['Expired warranty', 'Warranty ending in 30 days'] },
          { key: 'h5', primary: 'Look up anyone or anything', secondary: 'A person shows their devices, licence seats and other IDs', tone: 'info', chips: ['Who is QID447?', 'Find Microsoft 365'] },
        ],
      },
    ],
  };
}

function duplicates(d: Digest, q: string): Answer {
  // Most specific scope wins: serials, then people, then asset records, else everything.
  const wantSerial = has(q, RE.serial);
  const wantPeople = !wantSerial && (has(q, RE.person) || /\b(employee|emp) ?ids?\b/.test(q));
  const wantAssets = !wantSerial && !wantPeople && has(q, RE.assetWord);
  const all = !wantSerial && !wantPeople && !wantAssets;
  const showTags = wantAssets || all;
  const showSerials = wantSerial || wantAssets || all;
  const showPeople = wantPeople || all;

  const sections: AnswerSection[] = [];
  let count = 0;
  if (showTags) {
    count += d.duplicateAssetTags.length;
    for (const g of d.duplicateAssetTags)
      sections.push({ heading: `Asset tag ${g.key} · ${plural(g.assets.length, 'record')}`, rows: g.assets.map((a) => assetRow(a, { tone: 'danger' })) });
  }
  if (showSerials) {
    count += d.duplicateSerials.length;
    for (const g of d.duplicateSerials)
      sections.push({ heading: `Serial ${g.key} · ${plural(g.assets.length, 'record')}`, rows: g.assets.map((a) => assetRow(a, { tone: 'danger' })) });
  }
  if (showPeople) {
    let groups = d.duplicatePeople;
    if (/\bemails?\b/.test(q)) groups = groups.filter((g) => g.reason === 'email');
    else if (/\bnames?\b/.test(q)) groups = groups.filter((g) => g.reason === 'name');
    else if (/\bids?\b/.test(q) && wantPeople) groups = groups.filter((g) => g.reason === 'employee-id');
    count += groups.length;
    const label = { email: 'Same email', name: 'Same name', 'employee-id': 'Same employee ID' } as const;
    for (const g of groups) sections.push({ heading: `${label[g.reason]} · ${g.key}`, rows: g.people.map((p) => personRow(p, { tone: 'warn' })) });
  }

  const scope = [showTags && 'asset tags', showSerials && 'serial numbers', showPeople && 'employee records'].filter(Boolean).join(', ');
  return {
    title: 'Duplicate entries',
    count,
    tone: count ? 'danger' : 'ok',
    text: count
      ? `Found ${plural(count, 'duplicate group')} across ${scope}. Matching ignores case, spaces and - _ / so "SK-LT 01" and "sklt01" count as the same value.`
      : `No duplicates in ${scope}. Matching ignores case, spaces and - _ / characters, so near-identical values were checked too.`,
    sections,
    followUps: ['Employees with multiple IDs', 'Assets without a serial number'],
  };
}

function multiAssets(d: Digest, q: string): Answer {
  const laptopsOnly = has(q, RE.laptop);
  const min = Math.max(2, parseAtLeast(q) ?? Number(/(\d+)\s+(?:assets?|devices?|laptops?|items?)\b/.exec(q)?.[1] ?? 2));
  const rows = d.multiAssetHolders
    .filter((p) => (laptopsOnly ? p.laptops : p.devices) >= min)
    .sort((a, b) => (laptopsOnly ? b.laptops - a.laptops : b.devices - a.devices) || a.displayName.localeCompare(b.displayName));
  const noun = laptopsOnly ? 'laptop' : 'asset';
  return {
    title: `Employees with ${min} or more ${noun}s`,
    count: rows.length,
    tone: rows.length ? 'warn' : 'ok',
    text: rows.length
      ? `${plural(rows.length, 'person', 'people')} ${rows.length === 1 ? 'holds' : 'hold'} ${min} or more ${noun}s. Held items are listed under each name.`
      : `Nobody holds ${min} or more ${noun}s right now.`,
    sections: [
      {
        rows: rows.map((p) =>
          personRow(p, {
            badge: laptopsOnly ? plural(p.laptops, 'laptop') : plural(p.devices, 'asset'),
            tone: laptopsOnly || p.laptops > 1 ? 'warn' : 'info',
            chips: laptopsOnly ? p.assets.filter((a) => /laptop|notebook/i.test(a)) : p.assets,
          }),
        ),
      },
    ],
    link: laptopsOnly ? { label: 'Open in People', to: '/employees?devices=multi-laptop' } : undefined,
    followUps: laptopsOnly ? ['Employees with 3 or more assets'] : ['Employees with 2 or more laptops', 'Employees with 3 or more assets'],
  };
}

function multiIds(d: Digest): Answer {
  const rows = d.multiIdPeople;
  return {
    title: 'Employees with multiple IDs',
    count: rows.length,
    tone: rows.length ? 'info' : 'ok',
    text: rows.length
      ? `${plural(rows.length, 'person', 'people')} ${rows.length === 1 ? 'is' : 'are'} known by more than one employee ID (intern, overseas, contractor or previous IDs). The current ID is shown first.`
      : 'Everyone is recorded under a single employee ID.',
    sections: [{ rows: rows.map((p) => personRow(p, { badge: plural(p.otherIds.length + 1, 'ID'), chips: p.otherIds, tone: 'info' })) }],
    link: { label: 'Open in People', to: '/employees?quality=multi-id' },
    followUps: ['Duplicate employees', 'Employees with 2 or more assets'],
  };
}

function former(d: Digest, q: string): Answer {
  const wantSeats = has(q, RE.licence);
  const wantAssets = /\b(assets?|devices?|laptops?|hardware|equipment)\b/.test(q);
  const showAssets = wantAssets || !wantSeats;
  const showSeats = wantSeats || !wantAssets;
  const sections: AnswerSection[] = [];
  let count = 0;
  if (showAssets) {
    count += d.formerWithAssets.length;
    if (d.formerWithAssets.length)
      sections.push({ heading: 'Still holding hardware', rows: d.formerWithAssets.map((p) => personRow(p, { badge: plural(p.assets.length, 'asset'), chips: p.assets, tone: 'danger' })) });
  }
  if (showSeats) {
    count += d.formerWithSeats.length;
    if (d.formerWithSeats.length)
      sections.push({ heading: 'Still holding licence seats', rows: d.formerWithSeats.map((p) => personRow(p, { badge: plural(p.licences.length, 'seat'), chips: p.licences, tone: 'danger' })) });
  }
  return {
    title: 'Former employees still holding items',
    count,
    tone: count ? 'danger' : 'ok',
    text: count ? `${plural(count, 'former employee')} still ${count === 1 ? 'has' : 'have'} items recorded against them. Recover the items or release the seats.` : 'No former employee holds hardware or a licence seat.',
    sections,
  };
}

type LicenceRow = Digest['licences'][number];
const licenceRow = (l: LicenceRow): AnswerRow => ({
  key: `l-${l.id}`,
  code: l.licenseTag,
  primary: l.productName,
  secondary: [l.publisher, `Expiry ${fmtDate(l.expiryDate)}`, l.inForce ? null : `Status: ${l.status}`, l.seatsAssigned ? plural(l.seatsAssigned, 'named seat') : null].filter(Boolean).join(' · '),
  badge: daysText(l.daysLeft),
  tone: dayTone(l.daysLeft),
  to: `/software/licenses/${l.id}`,
});

function licences(d: Digest, q: string): Answer {
  const past = parsePastDays(q);
  const days = past === null ? parseDays(q) : null;
  // A general question ("how many licences") gets both: what has lapsed and what is due in 30 days.
  const general = !has(q, RE.expired) && !has(q, RE.expiring) && days === null && past === null;
  const wantExpired = general || past !== null || has(q, RE.expired);
  const wantUpcoming = general || !wantExpired || /\b(expiring|upcoming|soon|or expiring|and expiring|due)\b/.test(q) || days !== null;
  const window = days ?? (general ? 30 : 15);
  const sections: AnswerSection[] = [];
  let count = 0;
  const parts: string[] = [];
  if (wantExpired) {
    // Same definition as the licence insights: still in force but past the expiry date.
    const rows = d.licences.filter((l) => l.daysLeft < 0 && l.inForce && (past === null || l.daysLeft >= -past)).sort((a, b) => b.daysLeft - a.daysLeft);
    count += rows.length;
    parts.push(`${plural(rows.length, 'licence')} already expired`);
    if (rows.length) sections.push({ heading: 'Expired', rows: rows.map(licenceRow) });
  }
  if (wantUpcoming) {
    const rows = d.licences.filter((l) => l.daysLeft >= 0 && l.daysLeft <= window && l.inForce);
    count += rows.length;
    parts.push(`${plural(rows.length, 'licence')} in force ${window === 0 ? 'expiring today' : `expiring in the next ${plural(window, 'day')}`}`);
    const next = rows.length ? undefined : d.licences.find((l) => l.daysLeft > window && l.inForce);
    if (next) parts.push(`the next one due is ${next.productName} (${next.licenseTag}) ${daysText(next.daysLeft)}, on ${fmtDate(next.expiryDate)}`);
    if (rows.length) sections.push({ heading: window === 0 ? 'Expiring today' : `Expiring within ${plural(window, 'day')}`, rows: rows.map(licenceRow) });
  }
  return {
    title: wantExpired && !wantUpcoming ? 'Expired licences' : wantExpired ? 'Expired and expiring licences' : `Licences expiring within ${plural(window, 'day')}`,
    count,
    tone: count ? (wantExpired ? 'danger' : 'warn') : 'ok',
    text: `${parts.join('; ')} (counted from ${fmtDate(d.today)}, UTC).${count ? ' Renew, or confirm cancellation and update the status.' : ''}`,
    sections,
    link: wantExpired && !wantUpcoming ? { label: 'Open in Licences', to: '/software/licenses?expiry=expired&sort=expiryDate' } : { label: 'Open in Licences', to: `/software/licenses?expiry=${window <= 30 ? 30 : 90}&sort=expiryDate` },
    followUps: wantExpired && !wantUpcoming ? ['Licences expiring in 15 days', 'Licences expiring in 90 days'] : ['Show me expired licences', `Licences expiring in ${window < 30 ? 30 : 90} days`],
  };
}

type WarrantyRow = Digest['warranty'][number];
const warrantyRow = (a: WarrantyRow): AnswerRow =>
  assetRow(a, { key: `w-${a.id}`, badge: daysText(a.daysLeft), tone: dayTone(a.daysLeft), secondary: [a.assetType, a.assignee ? `with ${a.assignee}` : a.status, `Warranty ${fmtDate(a.warrantyExpiry)}`].join(' · ') });

function warranty(d: Digest, q: string): Answer {
  const past = parsePastDays(q);
  const days = past === null ? parseDays(q) : null;
  const wantExpired = past !== null || has(q, RE.expired);
  const wantUpcoming = !wantExpired || /\b(expiring|upcoming|soon|ending|or expiring|and expiring)\b/.test(q) || days !== null;
  const window = days ?? 30;
  const sections: AnswerSection[] = [];
  let count = 0;
  const parts: string[] = [];
  if (wantExpired) {
    const rows = d.warranty.filter((a) => a.daysLeft < 0 && (past === null || a.daysLeft >= -past)).sort((a, b) => b.daysLeft - a.daysLeft);
    count += rows.length;
    parts.push(`${plural(rows.length, 'asset')} out of warranty (within the last year)`);
    if (rows.length) sections.push({ heading: 'Warranty expired', rows: rows.map(warrantyRow) });
  }
  if (wantUpcoming) {
    const rows = d.warranty.filter((a) => a.daysLeft >= 0 && a.daysLeft <= window);
    count += rows.length;
    parts.push(`${plural(rows.length, 'asset')} reaching end of warranty in the next ${plural(window, 'day')}`);
    if (rows.length) sections.push({ heading: `Ending within ${plural(window, 'day')}`, rows: rows.map(warrantyRow) });
  }
  const unknown = d.totals.warrantyNotRecorded;
  return {
    title: wantExpired && !wantUpcoming ? 'Expired warranty' : `Warranty ending within ${plural(window, 'day')}`,
    count,
    tone: count ? (wantExpired ? 'danger' : 'warn') : 'ok',
    text: `${parts.join('; ')}. Retired assets are excluded.${unknown ? ` ${plural(unknown, 'asset')} ${unknown === 1 ? 'has' : 'have'} no warranty date recorded, so ${unknown === 1 ? 'it is' : 'they are'} not in this answer.` : ''}`,
    sections,
    link: { label: 'Open Warranty', to: wantExpired && !wantUpcoming ? '/warranty?state=expired' : window <= 30 ? '/warranty?state=within30' : '/warranty?sort=daysLeft' },
    followUps: ['Expired warranty', 'Warranty ending in 90 days'],
  };
}

function noSerial(d: Digest): Answer {
  const rows = d.noSerial;
  return {
    title: 'Assets without a serial number',
    count: rows.length,
    tone: rows.length ? 'warn' : 'ok',
    text: rows.length ? `${plural(rows.length, 'asset')} ${rows.length === 1 ? 'has' : 'have'} no serial number. Serials prove a device at audit and warranty claims.` : 'Every asset has a serial number.',
    sections: [{ rows: rows.map((a) => assetRow(a, { tone: 'warn' })) }],
    link: { label: 'Open in Assets', to: '/assets?quality=no-serial' },
  };
}

export interface HealthTile {
  key: string;
  label: string;
  count: number;
  tone: Tone;
  ask: string;
}

/** The checks shown as tiles when the assistant opens, worst first. */
export function healthTiles(d: Digest): HealthTile[] {
  const dupes = d.duplicateAssetTags.length + d.duplicateSerials.length + d.duplicatePeople.length;
  const expired = d.licences.filter((l) => l.daysLeft < 0 && l.inForce).length;
  const due15 = d.licences.filter((l) => l.daysLeft >= 0 && l.daysLeft <= 15 && l.inForce).length;
  const warranty30 = d.warranty.filter((a) => a.daysLeft >= 0 && a.daysLeft <= 30).length;
  const warrantyGone = d.warranty.filter((a) => a.daysLeft < 0).length;
  const formerHolding = d.formerWithAssets.length + d.formerWithSeats.length;
  return [
    { key: 'dup', label: 'Duplicate entries', count: dupes, tone: dupes ? 'danger' : 'ok', ask: 'Show all duplicates' },
    { key: 'lic-expired', label: 'Expired licences', count: expired, tone: expired ? 'danger' : 'ok', ask: 'Show me expired licences' },
    { key: 'lic-15', label: 'Licences due ≤ 15 days', count: due15, tone: due15 ? 'warn' : 'ok', ask: 'Licences expiring in 15 days' },
    { key: 'multi', label: 'People with 2+ assets', count: d.multiAssetHolders.length, tone: d.multiAssetHolders.length ? 'warn' : 'ok', ask: 'Employees with 2 or more assets' },
    { key: 'ids', label: 'People with multiple IDs', count: d.multiIdPeople.length, tone: d.multiIdPeople.length ? 'info' : 'ok', ask: 'Employees with multiple IDs' },
    { key: 'w30', label: 'Warranty ending ≤ 30 days', count: warranty30, tone: warranty30 ? 'warn' : 'ok', ask: 'Warranty ending in 30 days' },
    { key: 'w-expired', label: 'Warranty expired', count: warrantyGone, tone: warrantyGone ? 'danger' : 'ok', ask: 'Expired warranty' },
    { key: 'former', label: 'Former staff holding items', count: formerHolding, tone: formerHolding ? 'danger' : 'ok', ask: 'Former employees still holding items' },
    { key: 'serial', label: 'Assets without serial', count: d.noSerial.length, tone: d.noSerial.length ? 'warn' : 'ok', ask: 'Assets without a serial number' },
  ];
}

const TONE_RANK: Record<Tone, number> = { danger: 0, warn: 1, info: 2, neutral: 3, ok: 4 };

function overview(d: Digest): Answer {
  const tiles = healthTiles(d).sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone] || b.count - a.count);
  const open = tiles.filter((t) => t.count > 0);
  return {
    title: 'Data health summary',
    count: open.length,
    tone: open.some((t) => t.tone === 'danger') ? 'danger' : open.length ? 'warn' : 'ok',
    text: open.length
      ? `${plural(open.length, 'check')} of ${tiles.length} found something across ${num(d.totals.assets)} assets, ${num(d.totals.activeEmployees)} active employees and ${num(d.totals.licences)} licences. Pick one to see the exact records.`
      : `All ${tiles.length} checks are clear across ${num(d.totals.assets)} assets, ${num(d.totals.activeEmployees)} active employees and ${num(d.totals.licences)} licences.`,
    sections: [{ rows: tiles.map((t) => ({ key: t.key, primary: t.label, badge: t.count ? num(t.count) : 'Clear', tone: t.tone, chips: [t.ask] })) }],
  };
}

// ── Register lookup (people, assets, licences by name, ID, tag or serial) ──

/** Words that frame a lookup but are not part of what to find. */
const FILLER = new Set(
  'a an the of for to in on at by with and or is are was were be me my our we us i you show tell give find search look lookup up get list display open details detail info information about please can could would will do does did have has had hold holds holding who whose what which where when how many much named called name employee employees person people staff user asset assets device devices laptop laptops item items licence licences software record records profile id code tag serial number'.split(' '),
);

/** What to search for, or null when nothing specific is left ("show me details" names nobody). */
export function lookupTerm(question: string): string | null {
  const words = question
    .replace(/[?!.,;:"'()]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !FILLER.has(w.toLowerCase()));
  const term = words.join(' ').trim();
  return term.length >= 2 ? term.slice(0, 120) : null;
}

/** Answer for a register lookup. With exactly one person found, their full record is passed as `person`. */
export function lookupAnswer(term: string, r: SearchResults, person?: EmployeeDetail): Answer {
  if (person) {
    const seats = person.openSeats ?? [];
    const sections: AnswerSection[] = [];
    if (person.devices.length)
      sections.push({
        heading: `Devices held · ${person.devices.length}`,
        rows: person.devices.map((a) => ({
          key: `d-${a.id}`,
          code: a.assetTag,
          primary: a.name,
          secondary: [a.assetType, a.serialNumber ? `S/N ${a.serialNumber}` : null, a.assignedDate ? `since ${fmtDate(a.assignedDate)}` : null].filter(Boolean).join(' · '),
          tone: 'info' as Tone,
          to: `/assets/${a.id}`,
        })),
      });
    if (seats.length)
      sections.push({
        heading: `Licence seats · ${seats.length}`,
        rows: seats.map((s) => ({ key: `s-${s.seatId}`, code: s.licenseTag, primary: s.productName, secondary: `Assigned ${fmtDate(s.assignedAt)}`, tone: 'info' as Tone, to: `/software/licenses/${s.licenseId}` })),
      });
    if (person.aliases.length)
      sections.push({ heading: 'Other employee IDs', rows: [{ key: 'ids', primary: `Also known as ${plural(person.aliases.length, 'ID')}`, chips: person.aliases.map((x) => `${x.code} (${x.kind})`), tone: 'neutral' as Tone }] });
    const facts = [person.employeeCode, person.designation, person.team ?? 'No team recorded', person.email || 'No email recorded', person.role ? `Portal role: ${person.role}` : null].filter(Boolean).join(' · ');
    return {
      title: person.displayName,
      tone: person.isActive ? 'info' : 'warn',
      count: person.devices.length,
      text: `${facts}. ${person.isActive ? '' : 'Former employee. '}Holds ${plural(person.devices.length, 'device')}${seats.length ? ` and ${plural(seats.length, 'licence seat')}` : ''}.`,
      sections,
      link: { label: 'Open profile', to: `/employees/${person.id}` },
      followUps: person.devices.length > 1 ? ['Employees with 2 or more assets'] : undefined,
    };
  }
  const licences = r.licenses ?? [];
  const total = r.employees.length + r.assets.length + licences.length;
  const sections: AnswerSection[] = [];
  if (r.employees.length)
    sections.push({
      heading: 'People',
      rows: r.employees.map((e) => ({ key: `e-${e.id}`, code: e.employeeCode, primary: e.displayName, secondary: [e.team ?? 'No team recorded', e.email].filter(Boolean).join(' · '), badge: plural(e.deviceCount, 'device'), tone: 'info' as Tone, to: `/employees/${e.id}` })),
    });
  if (r.assets.length)
    sections.push({
      heading: 'Assets',
      rows: r.assets.map((a) => ({ key: `a-${a.id}`, code: a.assetTag, primary: a.name, secondary: [a.assetType, a.status, a.serialNumber ? `S/N ${a.serialNumber}` : null, a.assignee ? `with ${a.assignee.displayName}` : null].filter(Boolean).join(' · '), tone: 'info' as Tone, to: `/assets/${a.id}` })),
    });
  if (licences.length)
    sections.push({
      heading: 'Licences',
      rows: licences.map((l) => ({ key: `l-${l.id}`, code: l.licenseTag, primary: l.productName, secondary: [l.publisher, l.licenseType, l.expiryDate ? `Expiry ${fmtDate(l.expiryDate)}` : null].filter(Boolean).join(' · '), tone: 'info' as Tone, to: `/software/licenses/${l.id}` })),
    });
  if (!total)
    return {
      title: 'Nothing found',
      tone: 'neutral',
      text: `No person, asset or licence in the register matches "${term}". Try a name, employee ID, asset tag, serial number or product name, or ask about one of the checks.`,
      sections: [],
      followUps: ['Give me a data health summary', 'What can you do?'],
    };
  return {
    title: `Matches for "${term}"`,
    tone: 'info',
    count: total,
    text: `${[r.employees.length && plural(r.employees.length, 'person', 'people'), r.assets.length && plural(r.assets.length, 'asset'), licences.length && plural(licences.length, 'licence')].filter(Boolean).join(', ')} in the register. Open one for the full record, or name one person for their devices and seats.`,
    sections,
  };
}

function notUnderstood(question: string): Answer {
  return {
    title: 'I did not catch that',
    text: `"${question.slice(0, 80)}" is not one of my checks and nothing in the register matched it. Ask about duplicates, people holding several assets or IDs, licence expiry, warranty or missing serials, or name a person, asset tag, serial or product.`,
    tone: 'neutral',
    sections: [],
    followUps: ['Give me a data health summary', 'Show me expired licences', 'Employees with 2 or more assets', 'What can you do?'],
  };
}

/** Routes a question to the matching answer. Order matters: the most specific intent wins. */
export function answer(question: string, d: Digest): Answer {
  const q = normalise(question);
  if (!q || RE.help.test(q)) return helpAnswer();
  if (has(q, RE.duplicate)) return duplicates(d, q);
  if (has(q, RE.former)) return former(d, q);
  if (has(q, RE.multiId)) return multiIds(d);
  if (has(q, RE.serial) && has(q, RE.missing)) return noSerial(d);
  if (has(q, RE.warranty)) return warranty(d, q);
  if (has(q, RE.licence)) return licences(d, q);
  // "Which assets are going to expire" means hardware warranty.
  if (has(q, RE.assetWord) && /\bexpir\w*\b/.test(q)) return warranty(d, q);
  // People holding several assets needs a person and a count ("employees with 2 or more assets", "who holds the most laptops").
  const counted = parseAtLeast(q) !== null || has(q, RE.multiAsset) || /\b\d+\s+(?:assets?|devices?|laptops?|items?)\b/.test(q);
  if (has(q, RE.assetWord) && (has(q, RE.person) || /\bwho\b/.test(q)) && counted && !has(q, RE.age) && !has(q, RE.missing)) return multiAssets(d, q);
  if (/\bexpir\w*\b/.test(q)) return licences(d, q);
  if (has(q, RE.overview)) return overview(d);
  // Not a check: look the words up in the register (a name, employee ID, asset tag, serial or product).
  const term = lookupTerm(question);
  return term ? { ...notUnderstood(question.trim()), lookup: term } : notUnderstood(question.trim());
}

export { overview as overviewAnswer };
